// ตัวตนผู้ใช้: ตรวจ token กับ PaymentBroker (profile API) จากหน้าเว็บ แล้วสร้าง/ดึงผู้ใช้ใน /users — พอร์ตจาก Auth.gs
//  super_user = profile.user_type === "super_user" จากผลเดียวกัน (cache 10 นาทีในหน่วยความจำ)
import { ENDPOINTS } from "../config";
import { http, getAuthHeader } from "../apiClient";
import { fb, fk, err, num, nowIso, newId, monthKey, pick, getRow, updateRow, settings } from "./core";
import { ensureSeeded } from "./seed";

const NET_FAIL = "เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ";
const cache = new Map(); // header -> {at, bad, id}
export const resetAuthCache = () => cache.clear();

/** แปลง profile จาก Backend เป็นตัวตนที่ใช้ในระบบ (ตาม mapProfile ของ LevelUpCRM) */
export function identityFromProfile(d) {
  d = d || {};
  const cust = d.customer || {};
  const uid = cust.uid || d.uid || d.id || d._id || cust.email || d.email;
  const email = cust.email || d.email || "";
  const name = cust.name_th || d.full_name || d.email || (email ? String(email).split("@")[0] : "") || "ผู้ใช้";
  return { uid: String(uid || ""), email, name: String(name).slice(0, 20), photo: d.picture || "", userType: d.user_type || "" };
}

export async function verifyToken(header) {
  const hit = cache.get(header);
  if (hit && hit.until > Date.now()) {
    if (hit.bad) throw err("unauthorized", "token ไม่ถูกต้องหรือหมดอายุ");
    return { ...hit.id, fresh: false };
  }
  const res = await http.get(ENDPOINTS.profile);
  if (!res.status || !res.data) {
    if (res.message === NET_FAIL) throw err("auth_unavailable", "ตรวจสอบตัวตนไม่สำเร็จ กรุณาลองใหม่");
    cache.set(header, { bad: true, until: Date.now() + 30000 }); // จำผลปฏิเสธสั้น ๆ กันยิง Backend ซ้ำ
    throw err("unauthorized", "token ไม่ถูกต้องหรือหมดอายุ");
  }
  const id = identityFromProfile(res.data);
  if (!id.uid) throw err("unauthorized", "ไม่พบรหัสผู้ใช้ในโปรไฟล์");
  id.isSuper = id.userType === "super_user";
  cache.set(header, { id, until: Date.now() + 600000 });
  return { ...id, fresh: true };
}

export function newReferralCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let s = "";
  for (let i = 0; i < 8; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
  return s;
}

/** ผู้ใช้ (เติมค่าเริ่มต้นของฟิลด์ที่ RTDB ไม่เก็บเมื่อว่าง) */
export async function getUser(uid) {
  const u = await getRow(`users/${fk(uid)}`);
  if (!u) return null;
  return { ...u, preferences: u.preferences || {}, socialLinks: u.socialLinks || [], monthlyClaims: u.monthlyClaims || {} };
}

/** คืน ctx {uid, identity, user, isSuper} — ผู้ใช้ใหม่ถูกสร้างพร้อมคอยน์ต้อนรับ */
export async function authenticate() {
  const header = getAuthHeader();
  if (!/^Bearer\s+\S+$/i.test(header)) throw err("unauthorized", "กรุณาเข้าสู่ระบบ");
  const id = await verifyToken(header);
  await ensureSeeded();
  let user = await getUser(id.uid);
  if (!user) {
    const bonus = num((await settings()).signup_coins, 0);
    const code = newReferralCode();
    const row = {
      id: id.uid, email: id.email, displayName: id.name, username: "", photoURL: id.photo, coins: bonus,
      referralCode: code, userType: id.userType, banned: false, creatorEarnings: 0,
      vipPointsCur: 0, vipPointsPrev: 0, vipMonth: monthKey(), lifetimePoints: 0, streak: 0, longestStreak: 0,
      followerCount: 0, followingCount: 0, characterCount: 0, messageCount: 0, lastLoginAt: nowIso(),
    };
    const now = nowIso();
    const created = await fb.createOnly(`users/${fk(id.uid)}`, { ...row, createdAt: now, updatedAt: now });
    if (created) {
      await fb.put(`referral_codes/${code}`, id.uid);
      if (bonus > 0) {
        const txId = newId("tx");
        await fb.put(`coin_tx/${fk(id.uid)}/${txId}`, {
          id: txId, userId: id.uid, usageType: "signup", amount: bonus, balanceBefore: 0, balanceAfter: bonus, note: "โบนัสสมัครใหม่", createdAt: now, updatedAt: now,
        });
      }
    }
    user = await getUser(id.uid); // ถ้าอีกแท็บสร้างตัดหน้า จะได้แถวของแท็บนั้น (ไม่ซ้ำ ไม่แจกโบนัสสองรอบ)
  } else if (id.fresh) {
    // sync ข้อมูลจาก Backend เฉพาะตอน cache หมด (ไม่เขียนทุก request)
    user = { ...user, ...(await updateRow(`users/${fk(id.uid)}`, { email: id.email || user.email, userType: id.userType, lastLoginAt: nowIso() })) };
  }
  if (user.banned === true) throw err("banned", "บัญชีนี้ถูกระงับการใช้งาน" + (user.banReason ? ": " + user.banReason : ""));
  return { uid: id.uid, identity: id, user, isSuper: !!id.isSuper };
}

/** ส่งข้อมูลผู้ใช้ให้เจ้าของตัวเอง (ตัดฟิลด์ภายใน) */
export function publicSelf(u) {
  const o = { ...u };
  delete o.banReason;
  return o;
}

/** ข้อมูลโปรไฟล์สาธารณะของผู้ใช้อื่น */
export const publicUser = (u) =>
  pick(u, ["id", "displayName", "username", "photoURL", "bio", "backgroundImage", "socialLinks", "followerCount", "followingCount", "characterCount", "messageCount", "createdAt"]);

